import {
  AHIcon,
  AHSeparator,
  AHText,
  Box,
  ListItem,
  TouchableBox,
  useAHTextTypeStyles,
  useLayoutContext,
  useTheme,
} from "arkh";

import React, {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  graphql,
  useLazyLoadQuery,
  useMutation,
  useSubscription,
} from "react-relay";

import { useWorkspaceTodoEditAddImage } from "./useWorkspaceTodoEditAddImage";
import { WorkspaceTodoEditMainQuery } from "./__generated__/WorkspaceTodoEditMainQuery.graphql";
import {
  WorkspaceTodoEditRouteProp,
  WorkspaceTodoEditNavigationProp,
} from "../WorkspaceTodoEditStack";
import { WorkspaceTodoDetailIndicator } from "../../DraftIndicator";
import { OrgButton } from "../../../OrgButton";
import { useIntlTokens } from "../../../../../components/mobile/useIntlTokens";
import {
  TodoMainContent,
  TodoMainContentRef,
} from "./WorkspaceTodoEditMainContent";
import { LazyQueryNetworkBoundrary } from "../../../../../components/mobile/QueryNetworkBoundrary";
import {
  DropFileZone,
  droppedFileToImage,
} from "../../../../Files/DropFileZone";
import { WorkspaceTodoEditMainSubscription } from "./__generated__/WorkspaceTodoEditMainSubscription.graphql";
import { TodoPrioritySelector } from "./WorkspaceTodoEditMainPrioritySelector";
import { FormattedMessage } from "react-intl";
import { ExpireSelector } from "./WorkspaceTodoEditMainExpireSelector";
import { WorkspaceTodoEditMainResponseType } from "./WorkspaceTodoEditMainResponseType";
import { WebModalHeader } from "./WebModalHeader";
import { DeleteDraft } from "./WorkspaceTodoEditMainDelete";
import { EditedBy } from "./WorkspaceTodoEditMainEditedInfo";
import { WorkspaceTodoEditMainPushNotification } from "./WorkspaceTodoEditMainPushNotification";

import { WorkspaceTodoEditMainProducts } from "./WorkspaceTodoEditMainProducts";

import { ScrollView } from "react-native";
import { ReciversListItemButton } from "./WorkspaceRecieversListItemButton";
import { TemplateChooser } from "../../../WorkspaceTodoSignalShared/Templates/TemplateSelector";
import { FormattedDateInWorkspace } from "../../../../../common/dates/FormattedDate";
import { WorkspaceTodoEditMainCancelPublishMutation } from "./__generated__/WorkspaceTodoEditMainCancelPublishMutation.graphql";
import cuid from "cuid";

import { WorkspaceTodoEditMainSubTodos } from "./WorkspaceTodoEditMainSubTodos";
export const WorkspaceTodoEditMain = (props: {
  route: WorkspaceTodoEditRouteProp<"WorkspaceTodoEditMain">;
  navigation: WorkspaceTodoEditNavigationProp<"WorkspaceTodoEditMain">;
}) => (
  <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
    {(fk) => (
      <InnerWorkspaceTodoEditMain
        navigation={props.navigation}
        route={props.route}
        fetchKey={fk}
      />
    )}
  </LazyQueryNetworkBoundrary>
);

function InnerWorkspaceTodoEditMain(props: {
  route: WorkspaceTodoEditRouteProp<"WorkspaceTodoEditMain">;
  navigation: WorkspaceTodoEditNavigationProp<"WorkspaceTodoEditMain">;
  fetchKey: string;
}) {
  const data = useLazyLoadQuery<WorkspaceTodoEditMainQuery>(
    graphql`
      query WorkspaceTodoEditMainQuery(
        $id: ID!
        $workspaceId: ID!
        $createIfNotExist: Boolean!
        $parentId: ID
      ) {
        organisation {
          features
        }
        workspace(id: $workspaceId) @required(action: THROW) {
          id

          ...TemplateSelector_workspace
          todo(
            id: $id
            createIfNotExist: $createIfNotExist
            parentId: $parentId
          ) @required(action: THROW) {
            id
            status

            scheduledPublishAt {
              isoString
              ...FormattedDate
            }

            parent {
              title
              __typename
            }

            ...TemplateSelector_todo
            ...WorkspaceTodoEditMainSubTodos_todoWorkspace
            ...WorkspaceTodoEditMainProducts_TodoWorkspace
            ...WorkspaceTodoEditMainPrioritySelector_Todo
            ...WorkspaceTodoEditMainEditedInfo_Todo
            ...WorkspaceTodoEditMainContent_Todo
            ...WorkspaceTodoEditMainExpireSelector_Todo
            ...WorkspaceTodoEditMainResponseType_TodoWorkspace
            ...WorkspaceTodoEditMainPushNotification_todoWorkspace
            ...WorkspaceRecieversListItemButton_workspaceEntity
          }
        }
      }
    `,
    {
      id: props.route.params.id,
      workspaceId: props.route.params.workspaceId,
      createIfNotExist: props.route.params.create === true,
      parentId:
        props.route.params.create === true
          ? props.route.params.parentId
          : undefined,
    },
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );

  useSubscription<WorkspaceTodoEditMainSubscription>(
    useMemo(
      () => ({
        variables: {
          todoWorkspaceId: data.workspace.todo.id,
          workspaceId: data.workspace.id,
        },
        subscription: graphql`
          subscription WorkspaceTodoEditMainSubscription(
            $workspaceId: ID!
            $todoWorkspaceId: ID!
          ) {
            todoWorkspaceChanged(
              todoWorkspaceIds: [$todoWorkspaceId]
              workspaceId: $workspaceId
            ) {
              id
              status
              publishedAtDate {
                ...FormattedDate
              }
              updatedBySessionId
              formattedBody
              parent {
                title
                __typename
              }

              ...WorkspaceTodoEditMainSubTodos_todoWorkspace
              ...WorkspaceTodoEditMainPrioritySelector_Todo
              ...WorkspaceTodoEditMainEditedInfo_Todo
              ...WorkspaceTodoEditMainContent_Todo
              ...WorkspaceTodoEditMainExpireSelector_Todo
              ...WorkspaceTodoEditMainResponseType_TodoWorkspace
              ...WorkspaceTodoEditMainPushNotification_todoWorkspace
              ...WorkspaceRecieversListItemButton_workspaceEntity
            }
          }
        `,
      }),
      [data.workspace.id, data.workspace.todo.id]
    )
  );

  const todo = data.workspace.todo;
  const disabled = todo.status !== "DRAFT";

  const addImage = useWorkspaceTodoEditAddImage(todo.id);

  const [statuses, setStatuses] = useState<{
    [key: string]: "saving" | "saved" | "error";
  }>({});

  const setStatus = useCallback(
    (key: string, status: "saving" | "saved" | "error") => {
      setStatuses((statuses) => ({ ...statuses, [key]: status }));
    },
    []
  );

  const status = useMemo(() => {
    if (Object.values(statuses).some((val) => val === "error")) {
      return "error";
    }
    if (Object.values(statuses).some((val) => val === "saving")) {
      return "saving";
    }
    return "saved";
  }, [statuses]);

  const text = useAHTextTypeStyles("subtitle");
  const theme = useTheme();
  useLayoutEffect(() => {
    props.navigation.setOptions({
      header: () => (
        <WebModalHeader
          title={
            disabled ? null : (
              <>
                {todo.parent ? (
                  <TouchableBox
                    onPress={() => {
                      props.navigation.pop();
                    }}
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                    }}
                  >
                    <Box style={{ flex: 1, maxWidth: 200 }}>
                      <WebModalHeader.Title>
                        {todo.parent?.title}
                      </WebModalHeader.Title>
                    </Box>
                    <AHIcon
                      spaceRight="medium"
                      color="text-secondary"
                      type="chevron-right"
                    />
                  </TouchableBox>
                ) : null}
                <WebModalHeader.Title>
                  {todo.parent ? (
                    <FormattedMessage
                      defaultMessage="Edit Sub-todo"
                      description="Workspace edit sub-todo screen title"
                    />
                  ) : (
                    <FormattedMessage
                      defaultMessage="Edit Todo"
                      description="Workspace edit todo screen title"
                    />
                  )}
                </WebModalHeader.Title>

                {todo.status === "DRAFT" ? (
                  <TemplateChooser
                    workspace={data.workspace}
                    todoWorkspace={data.workspace.todo}
                  />
                ) : null}
              </>
            )
          }
          close={
            <>
              <>
                <EditedBy todo={data.workspace.todo} />
                <WorkspaceTodoDetailIndicator
                  status={data.workspace.todo.status}
                />
              </>
              <WebModalHeader.CloseButton onPress={props.navigation.goBack} />
            </>
          }
        />
      ),
    });
  }, [
    disabled,
    data.workspace,
    data.workspace.todo,
    props.navigation,
    props.route.params.workspaceId,
    status,
    text,
    theme.surface,
    todo.id,
    todo.status,
    todo.parent?.title,
    todo.parent,
  ]);

  const fileRef = useRef<TodoMainContentRef>(null);

  const intl_tokens = useIntlTokens();

  const [cancelPublish, cancelPublishInFlight] =
    useMutation<WorkspaceTodoEditMainCancelPublishMutation>(graphql`
      mutation WorkspaceTodoEditMainCancelPublishMutation($id: ID!)
      @raw_response_type {
        todoWorkspace(id: $id) {
          updateScheduledPublishAt(newPublishAt: null) {
            id
            status
            scheduledPublishAt {
              isoString
              ...FormattedDate
            }
          }
        }
      }
    `);

  const layout = useLayoutContext();

  return (
    <DropFileZone
      type="FILE_AND_IMAGE"
      deactivated={disabled}
      onDropFilesNew={async (files) => {
        files
          .filter((v) => v.abstractType === "IMAGE")
          .forEach(async ({ file, abstractType }) => {
            if (abstractType === "IMAGE") {
              const { width, height, uri } = await droppedFileToImage(file);
              addImage(uri, { width, height });
            }
          });

        const file_files = files.filter(
          ({ abstractType }) => abstractType === "FILE"
        );
        if (file_files.length > 0) {
          fileRef.current?.showFilesModal(file_files);
        }
      }}
      maxSize={10000000}
    >
      <Box style={{ flexDirection: "column", flex: 1 }}>
        <Box
          spaceBottom="none"
          color="background"
          style={{ flex: 1, flexDirection: "row" }}
        >
          <Box
            style={{ flex: 1, maxWidth: "60%" }}
            spaceLeft="medium"
            spaceVertical="medium"
            spaceRight="small"
          >
            <TodoMainContent
              ref={fileRef}
              todo={todo}
              workspaceId={props.route.params.workspaceId}
              setStatus={setStatus}
              disabled={disabled}
              addImage={addImage}
            />
          </Box>
          <ScrollView
            style={{ flex: 1, maxWidth: "40%" }}
            contentContainerStyle={{
              padding: layout.space.medium,
              paddingLeft: layout.space.small,
            }}
          >
            {data.organisation?.features.includes("PRODUCTS") ? (
              <Box spaceBottom="medium">
                <WorkspaceTodoEditMainProducts
                  todo={todo}
                  disabled={disabled}
                  openLinkProduct={({ linkedProductIds, todoWorkspaceId }) => {
                    props.navigation.navigateDeprecated(
                      "WorkspaceTodoEditLinkProduct",
                      {
                        linkedProductIds,
                        todoWorkspaceId,
                        workspaceId: props.route.params.workspaceId,
                      }
                    );
                  }}
                  openProduct={(id) => {
                    props.navigation.navigateDeprecated("WorkspaceProduct", {
                      id,
                    });
                  }}
                />
              </Box>
            ) : null}
            <ListItem
              disabled
              isFirst
              color={disabled ? "transparent" : "surface-primary"}
              border="border-primary"
              left={<AHIcon type="deadline" color="darkText" />}
              right={<ExpireSelector disabled={disabled} todo={todo} />}
            >
              <ListItem.TitleSecondary>
                {intl_tokens.Deadline}
              </ListItem.TitleSecondary>
            </ListItem>
            <AHSeparator />
            <ListItem
              disabled
              color={disabled ? "transparent" : "surface-primary"}
              border="border-primary"
              left={<AHIcon type="prio" color="darkText" />}
              right={<TodoPrioritySelector disabled={disabled} todo={todo} />}
              isLast
            >
              <ListItem.TitleSecondary>
                {intl_tokens.Priority}
              </ListItem.TitleSecondary>
            </ListItem>
            <Box spaceTop="medium">
              <WorkspaceTodoEditMainResponseType
                disabled={disabled}
                todo={todo}
                setStatus={setStatus}
              />
            </Box>
            {todo.parent ? null : (
              <>
                <Box spaceTop="medium">
                  <WorkspaceTodoEditMainPushNotification
                    disabled={disabled}
                    todoWorkspace={data.workspace.todo}
                  />
                </Box>
                <Box spaceTop="medium">
                  <ReciversListItemButton
                    onPress={() => {
                      props.navigation.navigateDeprecated(
                        "WorkspaceTodoReceivers",
                        {
                          workspaceId: props.route.params.workspaceId,
                          workspaceTodoId: todo.id,
                        }
                      );
                    }}
                    entity={data.workspace.todo}
                  />
                </Box>
              </>
            )}
            {todo.parent ? null : (
              <WorkspaceTodoEditMainSubTodos
                todo={todo}
                disabled={disabled}
                onOpenTodo={(id) => {
                  props.navigation.push("WorkspaceTodoEdit", {
                    workspaceId: props.route.params.workspaceId,
                    id,
                  });
                }}
                onCreateNewSubTodo={() => {
                  props.navigation.push("WorkspaceTodoEdit", {
                    workspaceId: props.route.params.workspaceId,
                    create: true,
                    id: cuid(),
                    parentId: todo.id,
                  });
                }}
              />
            )}
          </ScrollView>
        </Box>
      </Box>
      <Box
        style={{
          height: 60,
          width: "100%",
          justifyContent: "center",
        }}
        color="surface"
        spaceHorizontal="medium"
      >
        <Box
          style={{
            alignItems: "center",
            flexDirection: "row",
          }}
        >
          {todo.status === "DRAFT" ? (
            <Box spaceRight="medium">
              <DeleteDraft
                id={todo.id}
                onDeleted={() => {
                  props.navigation.goBack();
                }}
              />
            </Box>
          ) : null}

          <Box style={{ flex: 1 }} />
          {todo.parent ? (
            <Box spaceRight="small">
              <OrgButton
                backgroundColor="transparent"
                tintColor="primary"
                onPress={() => {
                  props.navigation.pop();
                }}
                title={
                  <FormattedMessage
                    defaultMessage="Back to main Todo"
                    description="Workspace Todo Back to main Todo button"
                  />
                }
              />
            </Box>
          ) : (
            <>
              {todo.status === "DRAFT" ? (
                <>
                  <Box spaceRight="small">
                    <OrgButton
                      backgroundColor="transparent"
                      tintColor="primary"
                      onPress={() => {
                        props.navigation.pop();
                      }}
                      title={
                        <FormattedMessage
                          defaultMessage="Continue later"
                          description="Workspace Todo Continue Later button"
                        />
                      }
                    />
                  </Box>

                  <OrgButton
                    backgroundColor="primary"
                    tintColor="text-inverted"
                    onPress={() => {
                      props.navigation.navigateDeprecated(
                        "WorkspaceTodoSchedule",
                        {
                          workspaceId: props.route.params.workspaceId,
                          workspaceTodoId: todo.id,
                        }
                      );
                    }}
                    title={
                      <FormattedMessage
                        defaultMessage="Schedule"
                        description="Workspace Todo Schedule button"
                      />
                    }
                  />
                </>
              ) : (
                <>
                  <Box spaceRight="small">
                    {todo.scheduledPublishAt ? (
                      <AHText spaceRight="small" type="subtitle">
                        <FormattedMessage
                          defaultMessage="Scheduled for publication: {formattedDate}"
                          values={{
                            formattedDate: (
                              <FormattedDateInWorkspace
                                date={todo.scheduledPublishAt}
                              />
                            ),
                          }}
                          description="Workspace Todo Schedule scheduled for publication"
                        />
                      </AHText>
                    ) : null}
                  </Box>

                  <OrgButton
                    backgroundColor="background"
                    tintColor="text-primary"
                    loading={cancelPublishInFlight}
                    onPress={() => {
                      cancelPublish({
                        optimisticResponse: {
                          todoWorkspace: {
                            updateScheduledPublishAt: {
                              id: todo.id,
                              status: "DRAFT",
                              scheduledPublishAt: null,
                            },
                          },
                        },
                        variables: {
                          id: todo.id,
                        },
                      });
                    }}
                    title={
                      <FormattedMessage
                        defaultMessage="Cancel publication"
                        description="Workspace Todo Schedule cancel publish button"
                      />
                    }
                  />
                </>
              )}
            </>
          )}
        </Box>
      </Box>
    </DropFileZone>
  );
}
